import React from "react";

import Highlighter from "react-highlight-words";

import {
  INVALID_REPORT_SPEC_FLAGS,
  SPEC_FLAGS_MAP,
} from "../../../Common/constants/SpecFlagConstants";
import { resolveDisplaySampleId } from "../../../Product/Report/Components/ReportSpecs/helper";
import LockedValue from "../../../grace-period/components/LockedValue";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import { isResultRevealingSpec } from "../../../grace-period/utils";

import { PRODUCT_ENVIRONMENT_CATEGORY } from "../../utils";

import { ReactComponent as EnvironmentIcon } from "../../../../assets/images/feature-icons/EnvironmentIconFilled.svg";
import { ReactComponent as ProductIcon } from "../../../../assets/images/feature-icons/ProductIconFilled.svg";

export const SUBMISSION_DETAILS_FETCH_DEFAULTS = {
  page: 1,
  sample_search: "",
  submission_search: "",
  search_by: "",
  date_from: "",
  date_to: "",
  user_selected_date_from: "",
  user_selected_date_to: "",
  submitted_by: "",
};

export const SCROLLBAR_STYLE = { height: "100%" };
export const EVENT_TYPE_TH_STYLE = { textAlign: "center" };

export const renderWithHighlight = (text, words) => {
  if (!text || !words.length) return text;
  return (
    <Highlighter
      highlightClassName="searchHighlight"
      searchWords={words}
      autoEscape
      textToHighlight={text}
    />
  );
};

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const formatTestsForSample = (sample) => {
  const parts = (sample.tests_list ?? [])
    .map((t) => (typeof t === "string" ? t : t?.Test))
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "";
};

export const getVisibleSampleFields = (sampleFields, sampleFieldsInfo) => sampleFields
  .filter((f) => sampleFieldsInfo[f] && f !== "po" && f !== "uuid" && f !== "sample_id");

export const filterSamplesBySearch = ({
  samples, query, visibleFields,
}) => {
  if (!samples?.length) return [];
  const q = query.trim().toLowerCase();
  if (!q) return [...samples];

  return samples.filter((sample) => {
    const fields = [
      ...visibleFields.map((f) => sample[f]),
      sample.sample_id,
      resolveDisplaySampleId(sample),
      formatTestsForSample(sample),
    ];
    return fields.some((v) => v && String(v).toLowerCase().includes(q));
  });
};

const sampleTypeBadgeClass = (value) => {
  const t = String(value).toLowerCase();
  if (t.includes("solid")) return "SubmissionDetailsSamplesList__typeBadge SubmissionDetailsSamplesList__typeBadge--solid";
  if (t.includes("liquid")) return "SubmissionDetailsSamplesList__typeBadge SubmissionDetailsSamplesList__typeBadge--liquid";
  if (t.includes("powder")) return "SubmissionDetailsSamplesList__typeBadge SubmissionDetailsSamplesList__typeBadge--powder";
  return "SubmissionDetailsSamplesList__typeBadge SubmissionDetailsSamplesList__typeBadge--default";
};

const fieldIsCentered = (field, sampleFieldsInfo) => {
  const title = String(sampleFieldsInfo[field].title_field).toLowerCase();
  return /item|time|best|size/.test(title);
};

const renderSampleStatus = (sample, areResultsLocked) => {
  const spec = SPEC_FLAGS_MAP[sample.specs_flag];
  if (!spec) return null;
  const statusInner = (
    <>
      <img src={spec.icon} alt="" />
      <span style={{ color: spec.color }}>{spec.label}</span>
    </>
  );
  if (areResultsLocked && isResultRevealingSpec(sample.specs_flag)) {
    return (
      <span className="SubmissionDetailsSamplesList__status">
        <LockedValue locked />
      </span>
    );
  }
  return (
    <span className="SubmissionDetailsSamplesList__status">
      {statusInner}
    </span>
  );
};

const renderSampleCategoryIcon = (sample, submissionSampleCategory) => {
  if (submissionSampleCategory !== PRODUCT_ENVIRONMENT_CATEGORY) return null;
  if (sample.sample_category === "product") {
    return <ProductIcon className="SubmissionDetailsSamplesList__categoryIcon" aria-label="Product sample" />;
  }
  if (sample.sample_category === "environment") {
    return <EnvironmentIcon className="SubmissionDetailsSamplesList__categoryIcon" aria-label="Environment sample" />;
  }
  return null;
};

export default function SubmissionDetailsSamplesTable({
  samples,
  filteredSamples,
  visibleFields,
  sampleFieldsInfo,
  highlightWords,
  submissionSampleCategory,
  onSampleClick,
}) {
  const { areResultsLocked } = useAccessStatus();

  if (!samples?.length) {
    return (
      <div className="SubmissionDetailsSamplesList SubmissionDetailsSamplesList--empty">
        No samples in this submission.
      </div>
    );
  }

  if (!filteredSamples.length) {
    return (
      <div className="SubmissionDetailsSamplesList SubmissionDetailsSamplesList--empty">
        No samples match your search.
      </div>
    );
  }

  const columnCount = visibleFields.length + 3;

  const renderHl = (raw) => {
    const text = formatCell(raw);
    return text === "" ? text : renderWithHighlight(text, highlightWords);
  };

  const showSampleCategoryIcon = submissionSampleCategory === PRODUCT_ENVIRONMENT_CATEGORY;

  return (
    <div className={`SubmissionDetailsSamplesList${showSampleCategoryIcon ? " SubmissionDetailsSamplesList--mixedCategory" : ""}`}>
      <table className="SubmissionDetailsSamplesList__table">
        <colgroup>
          {Array.from({ length: columnCount }, (_, i) => (
            <col key={i} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="SubmissionDetailsSamplesList__th SubmissionDetailsSamplesList__th--index" scope="col">#</th>
            {visibleFields.map((field) => (
              <th
                key={field}
                scope="col"
                className={`SubmissionDetailsSamplesList__th${fieldIsCentered(field, sampleFieldsInfo) ? " SubmissionDetailsSamplesList__th--center" : ""}`}
              >
                {sampleFieldsInfo[field].title_field}
              </th>
            ))}
            <th className="SubmissionDetailsSamplesList__th SubmissionDetailsSamplesList__th--tests" scope="col">Tests</th>
            <th className="SubmissionDetailsSamplesList__th SubmissionDetailsSamplesList__th--status" scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredSamples.map((sample, rowIdx) => {
            const disabled = INVALID_REPORT_SPEC_FLAGS.includes(sample.specs_flag);
            const testsSummary = formatTestsForSample(sample);
            return (
              <tr
                key={`${sample.sample_id}-${sample.sample_category}-${rowIdx}`}
                className={`SubmissionDetailsSamplesList__row${disabled ? " SubmissionDetailsSamplesList__row--disabled" : ""}`}
                onClick={() => !disabled && onSampleClick(sample)}
              >
                <td className="SubmissionDetailsSamplesList__td SubmissionDetailsSamplesList__td--index">
                  <span className="SubmissionDetailsSamplesList__indexCell">
                    {`${rowIdx + 1}.`}
                    {renderSampleCategoryIcon(sample, submissionSampleCategory)}
                  </span>
                </td>
                {visibleFields.map((field) => {
                  const raw = sample[field];
                  const className = `SubmissionDetailsSamplesList__td${fieldIsCentered(field, sampleFieldsInfo) ? " SubmissionDetailsSamplesList__td--center" : ""}`;
                  if (field === "sample_type") {
                    return (
                      <td key={field} className={className}>
                        <span className={sampleTypeBadgeClass(raw)}>{renderHl(raw)}</span>
                      </td>
                    );
                  }
                  return (
                    <td key={field} className={className} title={formatCell(raw)}>
                      {renderHl(raw)}
                    </td>
                  );
                })}
                <td className="SubmissionDetailsSamplesList__td SubmissionDetailsSamplesList__td--tests">
                  {testsSummary === "" ? testsSummary : renderWithHighlight(testsSummary, highlightWords)}
                </td>
                <td className="SubmissionDetailsSamplesList__td SubmissionDetailsSamplesList__td--status">
                  {renderSampleStatus(sample, areResultsLocked)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
